import type { IntelEntryRow } from '../model/intel.js';
import { DATA, DATA_TAG } from '../../theme/type.js';

/**
 * One entry (design 470–723's report reader).
 *
 * A headline and its fields, and **no clock** — `phase` is an integer 1–9 and
 * there is nothing else on this wire that says when anything happened. The
 * prototype's `time: 'T+02:10'` is deleted rather than approximated.
 *
 * ## The badge is drawn on two tiers of three
 *
 * `ROUTINE` draws nothing. A chip reading NORMAL beside four rows in five is a
 * mark that cannot distinguish anything, which is the same argument that took the
 * unknown-access pip off every friendly card in Phase 7. What the criticality
 * does instead is put the group it is in above or below the others
 * (`model/intel.ts`), which is a control that acts.
 *
 * ## `actor` and `contact` select rather than open a menu
 *
 * The right-click that every other row in this console answers is deliberately
 * **not** bound here. An entry is about a *resolved* turn: the asset it names may
 * since have been destroyed and the alias may name a contact that has dropped off
 * the picture, and `unitMenu` returns `null` for an id in neither list — so the
 * gesture would open on some rows and do nothing at all on visually identical
 * ones, which is the worst affordance this console could ship.
 *
 * Clicking the name instead is the console's own idiom and it is honest in the
 * case the menu is not: a subject that has left the view resolves to `MISSING`
 * and the rail says `NOT IN THIS PICTURE`, which is the truth about a report that
 * is a turn old.
 */

export interface EntryRowProps {
  readonly row: IntelEntryRow;
  readonly onSelect: (id: string) => void;
}

export function EntryRow(props: EntryRowProps): JSX.Element {
  const { row } = props;
  return (
    <div
      data-testid="intel-entry"
      data-entry-key={row.key}
      data-criticality={row.criticality}
      data-type={row.type}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '7px 12px 8px',
        borderBottom: '1px solid var(--rule-faint)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        {row.criticality !== 'ROUTINE' && (
          <span
            data-testid="intel-badge"
            style={{
              ...DATA_TAG,
              flex: 'none',
              fontSize: 9.5,
              padding: '1px 6px',
              border:
                '1px solid ' + (row.criticality === 'CRITICAL' ? 'var(--red-text)' : 'var(--warn)'),
              borderRadius: 'var(--radius)',
              whiteSpace: 'nowrap',
              color: row.criticality === 'CRITICAL' ? 'var(--red-text)' : 'var(--warn)',
            }}
          >
            {row.criticality}
          </span>
        )}
        <span
          data-testid="intel-entry-name"
          style={{
            ...DATA,
            flex: 'none',
            fontSize: 11.5,
            wordBreak: 'break-word',
            color: 'var(--text)',
          }}
        >
          {row.name}
        </span>

        {row.actor !== null && (
          <SubjectButton testId="intel-actor" id={row.actor} onSelect={props.onSelect} />
        )}
        {row.contact !== null && (
          <>
            <span style={{ flex: 'none', color: 'var(--text-muted)', fontSize: 11 }}>›</span>
            <SubjectButton testId="intel-contact" id={row.contact} onSelect={props.onSelect} />
          </>
        )}

        <span style={{ flex: 1 }} />

        {/* Null draws nothing at all. An entry with no outcome is not an entry
            with an unknown one — most event types carry no roll (M5-D10). */}
        {row.outcome !== null && (
          <span
            data-testid="intel-outcome"
            style={{
              ...DATA,
              flex: 'none',
              fontSize: 10.5,
              color:
                row.outcome === 'FAILURE' || row.outcome === 'COMPLICATION'
                  ? 'var(--warn)'
                  : 'var(--ok)',
            }}
          >
            {row.outcome}
          </span>
        )}
        <span
          data-testid="intel-phase"
          style={{ ...DATA_TAG, flex: 'none', color: 'var(--text-muted)' }}
        >
          P{row.phase}
        </span>
      </div>

      {row.fields.length > 0 && (
        <div
          data-testid="intel-fields"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}
        >
          {row.fields.map((field) => (
            <span
              key={field.key}
              data-testid="intel-field"
              data-field-key={field.key}
              style={{
                ...DATA,
                display: 'inline-flex',
                gap: 6,
                fontSize: 10.5,
                wordBreak: 'break-word',
              }}
            >
              <span style={{ color: 'var(--text-muted)' }}>{field.key}</span>
              <span style={{ color: 'var(--text-2)' }}>{field.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectButton(props: {
  testId: string;
  id: string;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={props.testId}
      onClick={() => props.onSelect(props.id)}
      style={{
        ...DATA,
        flex: 'none',
        padding: 0,
        background: 'transparent',
        border: 'none',
        wordBreak: 'break-word',
        // Blue, not accent: this is a link (§tokens.css's own job for the
        // colour) and not a selection. Cyan is spent only where clicking this
        // id would put a cyan ring around the thing that is now picked.
        color: 'var(--blue)',
        cursor: 'pointer',
      }}
    >
      {props.id}
    </button>
  );
}
